import React, { useEffect, useState, useCallback } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, CircularProgress, useMediaQuery, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabase.js';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from '../utils/theme';
import CssBaseline from '@mui/material/CssBaseline';

const PAGE_SIZE = 50;

const PACK_SELECT = `
  *,
  customers (
    *,
    pay (mois, annee, salaire),
    tax (revenu, annee_impots, situation, domiciliation, parts, autre_doc, pdf),
    identity (id, type_doc, qualite, noiretblanc, pdf, birthdate, expiry_date, race, country, "4_bords_visibles", civilite, autre_doc)
  )
`;

const moisMapping = {
    '1': 'Janvier', '2': 'Février', '3': 'Mars', '4': 'Avril', '5': 'Mai',
    '6': 'Juin', '7': 'Juillet', '8': 'Août', '9': 'Septembre', '10': 'Octobre',
    '11': 'Novembre', '12': 'Décembre'
};

const situationMapping = {
    'C': 'Célibataire', 'M': 'Marié', 'D': 'Divorcé/Séparé', 'V': 'Veuf', 'O': 'Pacsé'
};

const enrichPacks = (packData, bankList) =>
  packData.map(pack => ({
    ...pack,
    customers: pack.customers.map(customer => ({
      ...customer,
      nom_banque: bankList.find(b => b.Code_Banque === customer.code_banque)?.Banque || 'N/A'
    }))
  }));

const GenerateNotes = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const init = async () => {
      try {
        const { data: bankList, error: bankError } = await supabase
          .from('banque')
          .select('Code_Banque, Banque');

        if (bankError) {
          if (process.env.NODE_ENV !== 'production') console.error('Erreur banques:', bankError);
          setLoading(false);
          return;
        }

        setBanks(bankList);

        const { data: packData, error: packError } = await supabase
          .from('pack')
          .select(PACK_SELECT)
          .eq('statut', 'Actif')
          .order('id', { ascending: false })
          .range(0, PAGE_SIZE - 1);

        if (packError) {
          if (process.env.NODE_ENV !== 'production') console.error('Erreur packs:', packError);
          setLoading(false);
          return;
        }

        setData(enrichPacks(packData, bankList));
        setHasMore(packData.length === PAGE_SIZE);
        setOffset(PAGE_SIZE);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') console.error('Erreur lors de la récupération des données:', error);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const handleLoadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const { data: packData, error } = await supabase
        .from('pack')
        .select(PACK_SELECT)
        .eq('statut', 'Actif')
        .order('id', { ascending: false })
        .range(offset, offset + PAGE_SIZE - 1);

      if (!error) {
        setData(prev => [...prev, ...enrichPacks(packData, banks)]);
        setHasMore(packData.length === PAGE_SIZE);
        setOffset(prev => prev + PAGE_SIZE);
      } else if (process.env.NODE_ENV !== 'production') {
        console.error('Erreur chargement suivant:', error);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Erreur:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [offset, banks]);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return <Typography variant="h6">Aucune donnée trouvée</Typography>;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar position="static">
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
          <Button color="warning" onClick={() => navigate('/')}>
            Retour
          </Button>
          <Typography variant="h6" sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
            Notes Générées
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ padding: 2 }}>
        {data.map(pack => (
          <Paper key={pack.pack_id} elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
            <Typography variant="h6" gutterBottom>
              PACK {pack.pack_id} - Ajouté le {new Date(pack.created_at).toLocaleDateString('fr-FR')}
            </Typography>

            <Typography variant={isMobile ? 'body2' : 'subtitle1'}>
              <strong>Civilité :</strong> {pack.customers[0]?.identity[0]?.civilite || 'N.C.'} - {pack.customers[0]?.identity[0]?.country || 'N.C.'} - Né(e) en {pack.customers[0]?.identity[0]?.birthdate || 'N.C.'} - Domicilié : {pack.customers[0]?.tax[0]?.domiciliation || 'N.C.'}
            </Typography>

            <Typography variant={isMobile ? 'body2' : 'subtitle1'}>
              <strong>Identité :</strong> {pack.customers[0]?.identity[0]?.type_doc || 'N.C.'} - {pack.customers[0]?.identity[0]?.pdf ? 'PDF' : 'PHOTO'} - {pack.customers[0]?.identity[0]?.qualite || 'N.C.'} - Validité {pack.customers[0]?.identity[0]?.expiry_date || 'N.C.'}
            </Typography>

            <Typography variant={isMobile ? 'body2' : 'subtitle1'}>
              <strong>Impôts :</strong> {pack.customers[0]?.tax[0]?.annee_impots || 'N.C.'} sur {pack.customers[0]?.tax[0]?.annee_impots - 1} - {pack.customers[0]?.tax[0]?.pdf ? 'PDF' : 'PHOTO'} - {pack.customers[0]?.tax[0]?.revenu || 'N.C.'}€ revenu fiscal de référence - {situationMapping[pack.customers[0]?.tax[0]?.situation] || 'N.C.'}, {pack.customers[0]?.tax[0]?.parts || 'N.C.'} parts
            </Typography>

            <Typography variant={isMobile ? 'body2' : 'subtitle1'}>
              <strong>Banque :</strong> {pack.customers[0]?.nom_banque || 'N.C.'}
            </Typography>

            {pack.customers[0]?.pay && pack.customers[0].pay.length > 0 ? (
              <Typography variant={isMobile ? 'body2' : 'subtitle1'}>
                <strong>Fiche de paie :</strong> {pack.customers[0]?.pay_pdf ? 'PDF' : 'PHOTO'} - {pack.customers[0].pay.map(p => p.salaire ? `${moisMapping[p.mois]} ${p.salaire}€` : null).filter(Boolean).join(', ')}
              </Typography>
            ) : ('')}

            <Typography variant={isMobile ? 'body2' : 'subtitle1'}>
              <strong>Commentaire :</strong> {pack.commentaire || 'Aucun'}
            </Typography>

            <Typography variant="h6" sx={{ mt: 1 }}>
              Prix : {pack.price.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
            </Typography>

            <Typography variant={isMobile ? 'body2' : 'subtitle1'} sx={{ mt: 1 }}>
              <strong>Détails :</strong> <a href={`/view/${pack.pack_id}`} target="_blank" rel="noopener noreferrer">
                Voir plus de détails
              </a>
            </Typography>
          </Paper>
        ))}

        {hasMore && (
          <Box display="flex" justifyContent="center" sx={{ mt: 2, mb: 4 }}>
            <Button
              variant="contained"
              onClick={handleLoadMore}
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {loadingMore ? 'Chargement...' : 'Charger plus'}
            </Button>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default GenerateNotes;
